"use client";

import * as React from "react";
import {
  defaultHeaderLabels,
  type DesktopBreakpoint,
  type HeaderLabels,
  type HeaderNavigation,
  type HeaderVariant,
  type LinkComponentProps,
  type MobileMenuNavigation,
} from "./types";

type LinkComponent = React.ComponentType<LinkComponentProps> | "a";

type HeaderContextProviderProps = {
  children: React.ReactNode;
  fixtureData: HeaderNavigation;
  mobileMenuFixtureData: MobileMenuNavigation;
  disableMobileMenu?: boolean;
  disableLogoLink?: boolean;
  variant?: HeaderVariant;
  desktopBreakpoint?: DesktopBreakpoint;
  homeDomain?: string;
  translatedLabels?: boolean;
  LinkComponent?: LinkComponent;
  labels?: HeaderLabels;
};

type HeaderContextValue = {
  fixtureData: HeaderNavigation;
  mobileMenuFixtureData: MobileMenuNavigation;
  disableMobileMenu: boolean;
  disableLogoLink: boolean;
  /** Layout variant; `"default"` unless a consumer opts in. */
  variant: HeaderVariant;
  /** Width at which the floating variant swaps the drawer for the desktop row. */
  desktopBreakpoint: DesktopBreakpoint;
  /** The consuming app's own domain; links off it get an external affordance. */
  homeDomain?: string;
  /** The nav is carrying translated labels, which run longer than English. */
  translatedLabels: boolean;
  /** Optional override for how same-domain links render. */
  LinkComponent?: LinkComponent;
  /** Accessible labels for the header's own chrome; English unless injected. */
  labels: HeaderLabels;
};

const HeaderContext = React.createContext<HeaderContextValue | null>(null);

export const HeaderContextProvider = ({
  children,
  fixtureData,
  mobileMenuFixtureData,
  disableMobileMenu = false,
  disableLogoLink = false,
  variant = "default",
  desktopBreakpoint = "default",
  homeDomain,
  translatedLabels = false,
  LinkComponent,
  labels = defaultHeaderLabels,
}: HeaderContextProviderProps) => {
  return (
    <HeaderContext.Provider
      value={{
        fixtureData,
        mobileMenuFixtureData,
        disableMobileMenu,
        disableLogoLink,
        variant,
        desktopBreakpoint,
        homeDomain,
        translatedLabels,
        LinkComponent,
        labels,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
};

export const useHeaderContext = () => {
  const context = React.useContext(HeaderContext);
  if (!context) {
    throw new Error(
      "useHeaderContext must be used within HeaderContextProvider",
    );
  }
  return context;
};
